import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineStairs, MdOutlineStorefront } from 'react-icons/md';
import InvokeTabs from '../InvokeTabs/InvokeTabs';
import PlaybooksTeaser from '../../pages/Features/PlaybooksTeaser';
import { IntegrationsCatalog } from '../../pages/Services/Integrations/IntegrationsArea';
import usePlaybooks from '../../hooks/usePlaybooks';

/**
 * "Capacidades que puedes monetizar", bifurcada igual que la Tienda de la app:
 * Playbooks (el servicio entero, por etapas) y Catálogo (cada proceso suelto).
 *
 * Vive en un solo sitio y la usan la home y /platform, que hasta ahora tenían
 * ese bloque duplicado carácter a carácter. Ahora la sección tiene estado
 * (pestaña activa, teclado): dos copias se habrían separado en un mes.
 *
 * REGLA: la barra de pestañas SOLO existe si hay playbooks publicados. Sin
 * ellos —hoy, con el endpoint del core sin desplegar— la sección se pinta
 * exactamente como antes, con su titular de siempre y el catálogo. Una pestaña
 * "Playbooks" cuyo contenido diga que no hay playbooks es peor que no tener
 * pestañas, y encima quedaría horneada en el HTML estático (prerender.js).
 * El día que el core esté desplegado hay que RECONSTRUIR la web: el prerender
 * congela la respuesta de la API en el momento del build.
 */
const MonetizableCapabilities = () => {
   const { t, i18n } = useTranslation();
   const { playbooks, isLoading, hasError } = usePlaybooks();
   const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

   const hasPlaybooks = !isLoading && !hasError && playbooks.length > 0;

   const [picked, setPicked] = useState(null);

   // La pestaña por defecto es una FUNCIÓN PURA del estado de carga, así que se
   // calcula durante el render y no en un efecto: un efecto pasivo corre después
   // del commit, y se llegaba a pintar la barra con Catálogo activo un fotograma
   // antes de saltar a Playbooks.
   // Mientras no haya playbooks el panel visible es el catálogo, que es lo único
   // que hay. En cuanto los hay manda Playbooks, salvo que el visitante ya haya
   // elegido: entonces no se le mueve el contenido bajo el cursor.
   const active = picked || (hasPlaybooks ? 'playbooks' : 'catalog');

   const tabs = [
      { key: 'playbooks', label: t('shop_tab_playbooks'), icon: MdOutlineStairs },
      { key: 'catalog', label: t('shop_tab_catalog'), icon: MdOutlineStorefront },
   ];

   return (
      <section className="features__area pt-80 pb-80" style={{ background: '#f8f8fc' }}>
         <div className="container">
            <div className="row">
               <div className="col-xl-8 offset-xl-2 col-lg-10 offset-lg-1 text-center">
                  <h2 className="mb-20">
                     {hasPlaybooks ? t('caps_split_title') : t('platform_capabilities_title')}
                  </h2>
                  <p className="mb-40">
                     {hasPlaybooks
                        ? t('caps_split_subtitle')
                        : t('platform_capabilities_subtitle')}
                  </p>
               </div>
            </div>

            {/* Las tres ranuras de hijos son SIEMPRE las mismas, y las dos
                primeras valen null mientras no haya playbooks. Con un ternario
                que envolviera todo en un Fragment, React veria un cambio de tipo
                en la ranura del catalogo: lo desmontaria y volveria a pedirlo al
                core justo cuando llegan los playbooks. */}
            {hasPlaybooks ? (
               <InvokeTabs
                  tabs={tabs}
                  active={active}
                  onChange={setPicked}
                  ariaLabel={t('caps_tabs_aria')}
                  idPrefix="caps"
               />
            ) : null}

            {hasPlaybooks ? (
               <div
                  role="tabpanel"
                  id="caps-panel-playbooks"
                  aria-labelledby="caps-tab-playbooks"
                  tabIndex={0}
                  hidden={active !== 'playbooks'}
               >
                  <PlaybooksTeaser playbooks={playbooks} locale={locale} />
               </div>
            ) : null}

            {/* Los dos paneles se quedan montados y el inactivo se oculta con
                `hidden`: no se pierde el estado de las tarjetas al alternar y las
                dos mitades entran en el HTML prerenderizado. El envoltorio no
                lleva clases de grid: entre .container y el .row de
                IntegrationsCatalog no puede meterse padding. */}
            <div
               {...(hasPlaybooks
                  ? {
                       role: 'tabpanel',
                       id: 'caps-panel-catalog',
                       'aria-labelledby': 'caps-tab-catalog',
                       tabIndex: 0,
                    }
                  : {})}
               hidden={hasPlaybooks && active !== 'catalog'}
            >
               {hasPlaybooks ? <p className="caps__intro">{t('caps_catalog_intro')}</p> : null}
               <IntegrationsCatalog flattenCategories />
            </div>
         </div>
      </section>
   );
};

export default MonetizableCapabilities;
